/**
 * Сборка книги .xlsx с оформлением.
 *
 * Почему не SheetJS, который уже стоит в проекте. Его открытая сборка
 * (xlsx 0.17) умеет значения, объединения и ширину столбцов, но оформление
 * ячеек — шрифты, заливки, границы, выравнивание — молча выбрасывает при
 * записи: стили там относятся к платной версии. Именно поэтому выгрузка и
 * приходила «голой». Формат книги открытый, а нужная его часть невелика,
 * поэтому книга собирается здесь напрямую.
 *
 * Состав архива — минимальный набор, который Excel считает целой книгой:
 * типы содержимого, связи, сама книга, таблица стилей и один лист на
 * каждую таблицу. Общей таблицы строк (sharedStrings) нет: текст пишется
 * прямо в ячейку через inlineStr — на отчётах в сотни строк выигрыш от
 * общей таблицы незаметен, а частей в архиве и связей между ними меньше.
 */
import { zip } from './zip';
/* ── вспомогательное ───────────────────────────────────────────────── */
const esc = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    /* Управляющие символы в XML недопустимы: один такой символ,
       попавший из данных, делает книгу нечитаемой целиком. */
    .replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, '');
/** Номер столбца в буквенное обозначение: 0 → A, 26 → AA. */
export const colName = (index) => {
    let n = index;
    let out = '';
    do {
        out = String.fromCharCode(65 + (n % 26)) + out;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return out;
};
/**
 * Дата в число, как её хранит Excel.
 *
 * Отсчёт идёт от 30 декабря 1899 года: в Excel есть известная ошибка с
 * несуществующим 29 февраля 1900-го, и смещённая на день точка отсчёта —
 * общепринятый способ её обойти. Час берётся местный, иначе дата, набранная
 * в Бишкеке, в книге оказалась бы вчерашней.
 */
const dateSerial = (d) => {
    const local = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds());
    return (local - Date.UTC(1899, 11, 30)) / 86400000;
};
/* ── документная палитра ───────────────────────────────────────────── */
/**
 * Привести оформление к виду документа: чёрный текст, чёрные границы,
 * белый фон.
 *
 * Палитра портала рассчитана на экран: подписи серые, линии почти
 * невидимые — так таблица не рябит. На листе Excel и на бумаге эта
 * деликатность оборачивается нечитаемым бледным текстом и границами,
 * которых не видно. Поэтому цвет из оформления убирается, а всё
 * остальное — жирность, курсив, выравнивание, перенос, объединения,
 * ширины — остаётся как на экране.
 *
 * Выключается параметром mono: false, если книга нужна ровно в цветах
 * портала.
 */
const BLACK = 'FF000000';
/**
 * Рамка ячейки на бумаге.
 *
 * В вёрстке портала у ячеек только нижняя линия — вертикали на экране
 * рисует не таблица, а её фон; в книге на их месте оказывается пустота, и
 * колонки сливаются. Поэтому там, где рамка была хоть с одной стороны,
 * достраиваем остальные тонкими: получается обычная печатная таблица.
 * У ячеек вовсе без рамки — например у строк шапки документа — ничего не
 * появляется.
 */
const documentBorder = (b) => {
    if (!b || !(b.top || b.right || b.bottom || b.left))
        return undefined;
    const side = (s) => ({ style: (s === null || s === void 0 ? void 0 : s.style) || 'thin', color: BLACK });
    return { top: side(b.top), right: side(b.right), bottom: side(b.bottom), left: side(b.left) };
};
const toDocument = (style) => {
    if (!style)
        return style;
    return Object.assign(Object.assign({}, style), { font: style.font && Object.assign(Object.assign({}, style.font), { color: BLACK }), 
        /* Заливки нет вовсе: лист остаётся белым. */
        fill: undefined, border: documentBorder(style.border) });
};
/* ── таблица стилей ────────────────────────────────────────────────── */
/**
 * Собирает styles.xml, попутно раздавая номера стилей ячейкам.
 *
 * Одинаковое оформление в таблице встречается сотнями раз, поэтому каждый
 * шрифт, заливка, рамка и их сочетание заводятся один раз, а ячейки
 * ссылаются на номер. Иначе книга на пятьсот строк весила бы мегабайты.
 */
class StyleTable {
    constructor(mono = true) {
        this.fonts = new Map();
        this.fills = new Map();
        this.borders = new Map();
        this.numFmts = new Map();
        this.xfs = new Map();
        this.fontXml = [];
        this.fillXml = [];
        this.borderXml = [];
        this.numFmtXml = [];
        this.xfXml = [];
        this.mono = mono;
        /* Нулевые записи обязательны: на них ссылается стиль по умолчанию. */
        this.fontXml.push('<font><sz val="11"/><name val="Calibri"/></font>');
        this.fonts.set('', 0);
        /* Первые две заливки Excel считает служебными и требует именно в
           таком порядке — none, затем gray125. */
        this.fillXml.push('<fill><patternFill patternType="none"/></fill>');
        this.fillXml.push('<fill><patternFill patternType="gray125"/></fill>');
        this.fills.set('', 0);
        this.borderXml.push('<border><left/><right/><top/><bottom/><diagonal/></border>');
        this.borders.set('', 0);
        this.xfXml.push('<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>');
        this.xfs.set('', 0);
    }
    font(f) {
        var _a;
        const key = f ? JSON.stringify(f) : '';
        if (!f)
            return 0;
        const hit = this.fonts.get(key);
        if (hit !== undefined)
            return hit;
        const parts = [
            f.bold ? '<b/>' : '',
            f.italic ? '<i/>' : '',
            f.underline ? '<u/>' : '',
            f.strike ? '<strike/>' : '',
            `<sz val="${(_a = f.size) !== null && _a !== void 0 ? _a : 11}"/>`,
            f.color ? `<color rgb="${f.color}"/>` : '',
            `<name val="${esc(f.name || 'Calibri')}"/>`
        ];
        this.fontXml.push(`<font>${parts.join('')}</font>`);
        const id = this.fontXml.length - 1;
        this.fonts.set(key, id);
        return id;
    }
    fill(color) {
        if (!color)
            return 0;
        const hit = this.fills.get(color);
        if (hit !== undefined)
            return hit;
        this.fillXml.push(`<fill><patternFill patternType="solid"><fgColor rgb="${color}"/><bgColor indexed="64"/></patternFill></fill>`);
        const id = this.fillXml.length - 1;
        this.fills.set(color, id);
        return id;
    }
    border(b) {
        const key = b ? JSON.stringify(b) : '';
        if (!b || !key || key === '{}')
            return 0;
        const hit = this.borders.get(key);
        if (hit !== undefined)
            return hit;
        const side = (name, s) => s ? `<${name} style="${s.style}"><color rgb="${s.color}"/></${name}>` : `<${name}/>`;
        /* Порядок сторон в схеме строгий: left, right, top, bottom. */
        this.borderXml.push(`<border>${side('left', b.left)}${side('right', b.right)}${side('top', b.top)}${side('bottom', b.bottom)}<diagonal/></border>`);
        const id = this.borderXml.length - 1;
        this.borders.set(key, id);
        return id;
    }
    numFmt(code) {
        if (!code)
            return 0;
        const hit = this.numFmts.get(code);
        if (hit !== undefined)
            return hit;
        /* Номера до 163 заняты встроенными форматами Excel. */
        const id = 164 + this.numFmts.size;
        this.numFmts.set(code, id);
        this.numFmtXml.push(`<numFmt numFmtId="${id}" formatCode="${esc(code)}"/>`);
        return id;
    }
    /** Номер стиля для ячейки; одинаковые сочетания заводятся один раз. */
    id(raw) {
        /* Цвета приводим к документным здесь, а не при чтении таблицы:
           тогда правило действует и на листы, собранные вручную. */
        const style = this.mono ? toDocument(raw) : raw;
        if (!style)
            return 0;
        const key = JSON.stringify(style);
        const hit = this.xfs.get(key);
        if (hit !== undefined)
            return hit;
        const fontId = this.font(style.font);
        const fillId = this.fill(style.fill);
        const borderId = this.border(style.border);
        const numFmtId = this.numFmt(style.numFmt);
        const a = style.align;
        const alignment = a
            ? `<alignment${a.h ? ` horizontal="${a.h}"` : ''}${a.v ? ` vertical="${a.v}"` : ''}${a.wrap ? ' wrapText="1"' : ''}${a.rotate ? ` textRotation="${a.rotate}"` : ''}${a.indent ? ` indent="${a.indent}"` : ''}/>`
            : '';
        this.xfXml.push(`<xf numFmtId="${numFmtId}" fontId="${fontId}" fillId="${fillId}" borderId="${borderId}" xfId="0"` +
            ` applyFont="1" applyFill="1" applyBorder="1"${alignment ? ' applyAlignment="1"' : ''}${numFmtId ? ' applyNumberFormat="1"' : ''}>${alignment}</xf>`);
        const id = this.xfXml.length - 1;
        this.xfs.set(key, id);
        return id;
    }
    xml() {
        return ('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
            (this.numFmtXml.length
                ? `<numFmts count="${this.numFmtXml.length}">${this.numFmtXml.join('')}</numFmts>`
                : '') +
            `<fonts count="${this.fontXml.length}">${this.fontXml.join('')}</fonts>` +
            `<fills count="${this.fillXml.length}">${this.fillXml.join('')}</fills>` +
            `<borders count="${this.borderXml.length}">${this.borderXml.join('')}</borders>` +
            '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
            `<cellXfs count="${this.xfXml.length}">${this.xfXml.join('')}</cellXfs>` +
            '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
            '</styleSheet>');
    }
}
/* ── лист ──────────────────────────────────────────────────────────── */
const sheetXml = (sheet, styles) => {
    var _a, _b;
    const width = sheet.rows.reduce((m, r) => Math.max(m, r.length), 0);
    const dimension = `A1:${colName(Math.max(0, width - 1))}${Math.max(1, sheet.rows.length)}`;
    /* Закреплённая шапка: без неё в отчёте на пятьсот строк на втором
       экране уже не видно, что за колонка. */
    const freeze = sheet.freezeRows
        ? `<pane ySplit="${sheet.freezeRows}" topLeftCell="A${sheet.freezeRows + 1}" activePane="bottomLeft" state="frozen"/>`
        : '';
    const cols = ((_a = sheet.cols) === null || _a === void 0 ? void 0 : _a.length)
        ? `<cols>${sheet.cols
            .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w.toFixed(2)}" customWidth="1"/>`)
            .join('')}</cols>`
        : '';
    const rows = sheet.rows
        .map((row, r) => {
        var _a;
        const height = (_a = sheet.rowHeights) === null || _a === void 0 ? void 0 : _a[r];
        const cells = row
            .map((cell, c) => {
            const ref = `${colName(c)}${r + 1}`;
            const s = styles.id(cell.style);
            const attr = `r="${ref}"${s ? ` s="${s}"` : ''}`;
            if (cell.value === null || cell.value === undefined || cell.value === '') {
                /* Пустую ячейку всё равно пишем: на ней держатся
                   заливка и рамки, без которых таблица разъедется. */
                return s ? `<c ${attr}/>` : '';
            }
            if (cell.value instanceof Date)
                return `<c ${attr}><v>${dateSerial(cell.value)}</v></c>`;
            if (typeof cell.value === 'number')
                return `<c ${attr}><v>${cell.value}</v></c>`;
            return `<c ${attr} t="inlineStr"><is><t xml:space="preserve">${esc(cell.value)}</t></is></c>`;
        })
            .join('');
        return `<row r="${r + 1}"${height ? ` ht="${height.toFixed(2)}" customHeight="1"` : ''}>${cells}</row>`;
    })
        .join('');
    const merges = ((_b = sheet.merges) === null || _b === void 0 ? void 0 : _b.length)
        ? `<mergeCells count="${sheet.merges.length}">${sheet.merges
            .map(m => `<mergeCell ref="${colName(m.c1)}${m.r1 + 1}:${colName(m.c2)}${m.r2 + 1}"/>`)
            .join('')}</mergeCells>`
        : '';
    /* Порядок элементов внутри worksheet задан схемой и не произволен:
       dimension, sheetViews, cols, sheetData, mergeCells. */
    return ('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
        `<dimension ref="${dimension}"/>` +
        `<sheetViews><sheetView workbookViewId="0">${freeze}</sheetView></sheetViews>` +
        '<sheetFormatPr defaultRowHeight="15"/>' +
        cols +
        `<sheetData>${rows}</sheetData>` +
        merges +
        '</worksheet>');
};
/* ── книга целиком ─────────────────────────────────────────────────── */
/** Имя листа: Excel не принимает : \ / ? * [ ] и больше 31 знака. */
const sheetName = (name, index) => {
    const clean = String(name || '')
        .replace(/[:\\/?*[\]]/g, ' ')
        .trim()
        .slice(0, 31);
    return clean || `Лист${index + 1}`;
};
export const buildXlsx = (sheets, mono = true) => {
    const styles = new StyleTable(mono);
    const names = sheets.map((s, i) => sheetName(s.name, i));
    const entries = [
        {
            name: '[Content_Types].xml',
            content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
                '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
                '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
                '<Default Extension="xml" ContentType="application/xml"/>' +
                '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
                '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
                sheets
                    .map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
                    .join('') +
                '</Types>'
        },
        {
            name: '_rels/.rels',
            content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
                '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
                '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
                '</Relationships>'
        },
        {
            name: 'xl/workbook.xml',
            content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
                '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
                ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
                '<sheets>' +
                names.map((n, i) => `<sheet name="${esc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') +
                '</sheets></workbook>'
        },
        {
            name: 'xl/_rels/workbook.xml.rels',
            content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
                '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
                sheets
                    .map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`)
                    .join('') +
                `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
                '</Relationships>'
        }
    ];
    /* Листы собираем до таблицы стилей: пока их разбираем, в неё
       добавляются шрифты, заливки и рамки. */
    const built = sheets.map(s => sheetXml(s, styles));
    built.forEach((content, i) => entries.push({ name: `xl/worksheets/sheet${i + 1}.xml`, content }));
    entries.push({ name: 'xl/styles.xml', content: styles.xml() });
    return zip(entries);
};
